import type { CreateForecastScenarioInput, ForecastScenario } from '@taler/api-client'
import { forecastKeys } from '@taler/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { problemDetail } from '../internal/problem.ts'

export interface UseForecastScenariosOptions {
  readonly ledgerId: () => string | undefined
  readonly enabled: () => boolean
}

export interface UseForecastScenarios {
  readonly scenarios: ComputedRef<readonly ForecastScenario[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly isCreating: ComputedRef<boolean>
  readonly hasCreateError: ComputedRef<boolean>
  readonly createErrorDetail: ComputedRef<string | undefined>
  readonly deletingId: ComputedRef<string | undefined>
  readonly hasDeleteError: ComputedRef<boolean>
  readonly deleteErrorDetail: ComputedRef<string | undefined>
  create(input: CreateForecastScenarioInput): Promise<ForecastScenario>
  remove(scenarioId: string): Promise<void>
  readonly retrying: ComputedRef<boolean>
  retry(): void
}

export function useForecastScenarios(options: UseForecastScenariosOptions): UseForecastScenarios {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const scenariosQuery = useQuery({
    queryKey: computed(() => forecastKeys.scenarios(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.forecast.listScenarios(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  function invalidate(): void {
    for (const key of forecastKeys.invalidatedByScenarioWrite(options.ledgerId() ?? '')) {
      void queryClient.invalidateQueries({ queryKey: key })
    }
  }

  const createMutation = useMutation({
    mutationFn: (input: CreateForecastScenarioInput) =>
      api.forecast.createScenario(options.ledgerId()!, input),
    onSuccess: invalidate,
  })

  const deleteMutation = useMutation({
    mutationFn: (scenarioId: string) => api.forecast.deleteScenario(options.ledgerId()!, scenarioId),
    onSuccess: invalidate,
  })

  return {
    scenarios: computed(() => scenariosQuery.data.value ?? []),
    isLoading: computed(() => active.value && scenariosQuery.isPending.value),
    error: computed(() => scenariosQuery.error.value),
    retrying: retrying(scenariosQuery),
    isCreating: computed(() => createMutation.isPending.value),
    hasCreateError: computed(() => createMutation.error.value !== null),
    createErrorDetail: computed(() => problemDetail(createMutation.error.value)),
    deletingId: computed(() =>
      deleteMutation.isPending.value ? (deleteMutation.variables.value as string | undefined) : undefined,
    ),
    hasDeleteError: computed(() => deleteMutation.error.value !== null),
    deleteErrorDetail: computed(() => problemDetail(deleteMutation.error.value)),
    create: (input) => createMutation.mutateAsync(input),
    remove: (scenarioId) => deleteMutation.mutateAsync(scenarioId),
    retry: () => {
      void scenariosQuery.refetch()
    },
  }
}
